import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon, BanknotesIcon, AcademicCapIcon,
  BookOpenIcon, CheckCircleIcon, ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sd * { box-sizing:border-box; }
  .sd { font-family:'Sora',sans-serif; color:#1a1d2e; animation:sdFade .4s ease both; }
  @keyframes sdFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .sd-welcome { margin-bottom:1.5rem; }
  .sd-welcome-name { font-size:1.4rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .sd-welcome-sub  { font-size:.78rem; color:#9094a8; margin-top:4px; }

  /* Fee status banner */
  .fee-banner { border-radius:14px; padding:1rem 1.2rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:.8rem; cursor:pointer; transition:all .15s; }
  .fee-banner:hover { transform:translateY(-1px); }
  .fee-banner.overdue { background:linear-gradient(135deg,#fef2f2,#fff1f0); border:1px solid #fecaca; }
  .fee-banner.pending { background:linear-gradient(135deg,#fffbeb,#fef9ec); border:1px solid #fde68a; }
  .fee-banner.clear   { background:linear-gradient(135deg,#ecfdf5,#f0fdf4); border:1px solid #a7f3d0; }
  .fb-left  { display:flex; align-items:center; gap:12px; }
  .fb-icon  { width:40px; height:40px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .fb-icon svg { width:18px; height:18px; stroke-width:1.8; }
  .fb-icon.overdue { background:#fecaca; color:#ef4444; }
  .fb-icon.pending { background:#fde68a; color:#f59e0b; }
  .fb-icon.clear   { background:#a7f3d0; color:#10c98e; }
  .fb-title { font-size:.88rem; font-weight:700; }
  .fb-sub   { font-size:.72rem; margin-top:2px; }
  .fb-amount { font-family:'JetBrains Mono',monospace; font-size:1.1rem; font-weight:700; flex-shrink:0; }

  /* Stat cards */
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; cursor:pointer; transition:all .15s; }
  .stat-card:hover { transform:translateY(-1px); box-shadow:0 4px 14px rgba(0,0,0,.07); }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.green{background:#ecfdf5;color:#10c98e} .s-icon.blue{background:#eef2ff;color:#3b6ef8}
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.purple{background:#f3f0ff;color:#8b5cf6}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .s-value { font-size:1.5rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.1; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }

  /* Attendance progress */
  .progress-bar  { height:8px; background:#f0f2f7; border-radius:99px; overflow:hidden; margin-top:6px; }
  .progress-fill { height:100%; border-radius:99px; transition:width .6s ease; }

  /* Two col panels */
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head  { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-link  { font-size:.74rem; color:#3b6ef8; cursor:pointer; font-weight:600; white-space:nowrap; }
  .panel-link:hover { text-decoration:underline; }

  .list-item { display:flex; align-items:flex-start; gap:10px; padding:11px 18px; border-bottom:1px solid #f5f6fb; }
  .list-item:last-child { border-bottom:none; }
  .list-item:hover { background:#fafbff; }
  .list-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-top:5px; }
  .list-dot.blue{background:#3b6ef8} .list-dot.green{background:#10c98e}
  .list-dot.amber{background:#f59e0b} .list-dot.purple{background:#8b5cf6}
  .list-title { font-size:.8rem; font-weight:600; color:#1a1d2e; }
  .list-sub   { font-size:.7rem; color:#9094a8; margin-top:2px; }

  /* Results mini table */
  .results-list { padding:0 18px 14px; }
  .result-row { display:flex; align-items:center; justify-content:space-between; padding:8px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .result-row:last-child { border-bottom:none; }
  .result-subject { font-size:.8rem; font-weight:600; color:#1a1d2e; flex:1; }
  .result-type    { font-size:.68rem; color:#9094a8; background:#f0f2f7; padding:2px 7px; border-radius:99px; white-space:nowrap; }
  .result-score   { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:700; min-width:60px; text-align:right; }
  .result-bar-wrap { width:60px; height:6px; background:#f0f2f7; border-radius:99px; overflow:hidden; }
  .result-bar-fill { height:100%; border-radius:99px; }

  /* Exam countdown cards */
  .exam-list { padding:10px; display:flex; flex-direction:column; gap:8px; }
  .exam-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:11px 13px; display:flex; align-items:center; justify-content:space-between; gap:10px; }
  .exam-info  { flex:1; min-width:0; }
  .exam-name  { font-size:.82rem; font-weight:700; color:#0f1123; }
  .exam-meta  { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .exam-countdown { text-align:center; flex-shrink:0; }
  .countdown-val   { font-family:'JetBrains Mono',monospace; font-size:1.1rem; font-weight:700; }
  .countdown-label { font-size:.62rem; color:#9094a8; font-weight:600; text-transform:uppercase; }

  .sd-loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  @media (max-width:900px) { .stat-grid{grid-template-columns:repeat(2,1fr);} .two-col{grid-template-columns:1fr;} }
  @media (max-width:768px) { .sd-welcome-name{font-size:1.2rem;} .fee-banner{flex-direction:column;align-items:flex-start;} }
  @media (max-width:480px) { .stat-grid{grid-template-columns:1fr 1fr;} }
`;

const fmtMoney=(n)=>`Rs. ${Number(n||0).toLocaleString('en-PK')}`;
const fmtDate=(d)=>new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short'});

const StudentDashboard = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get('/student/dashboard');
      setData(res);
    } catch { toast.error('Failed to load dashboard'); }
    finally  { setLoading(false); }
  };

  if (loading) return (
    <div className="sd"><style>{style}</style>
      <div className="sd-loading"><div className="spinner"/>Loading…</div>
    </div>
  );

  const { student, attendance, feeStatus, recentResults, upcomingExams, recentNotices, pendingLeaves, quizSummary } = data || {};

  // Fee banner config
  const feeOverdueAmt = feeStatus?.overdueAmount || 0;
  const feePendingAmt = feeStatus?.pendingAmount  || 0;
  const feeType = feeOverdueAmt > 0 ? 'overdue' : feePendingAmt > 0 ? 'pending' : 'clear';
  const feeTitle = { overdue:'Fee Overdue!', pending:'Fee Due Soon', clear:'No Fees Due' }[feeType];
  const feeColor = { overdue:'#ef4444', pending:'#f59e0b', clear:'#10c98e' }[feeType];
  const feeAmt   = feeOverdueAmt > 0 ? feeOverdueAmt : feePendingAmt;
  const feeSubtitle = {
    overdue: `${feeStatus?.overdueCount || 0} overdue payment${feeStatus?.overdueCount!==1?'s':''}`,
    pending: `Due: ${feeStatus?.nextDueDate ? fmtDate(feeStatus.nextDueDate) : 'soon'}`,
    clear:   'All payments up to date',
  }[feeType];

  const attPct = attendance?.totalDays
    ? Math.round((attendance.present / attendance.totalDays) * 100) : 0;

  return (
    <div className="sd"><style>{style}</style>

      <div className="sd-welcome">
        <div className="sd-welcome-name">Welcome back, {student?.name?.split(' ')[0]} 👋</div>
        <div className="sd-welcome-sub">
          {student?.class} {student?.section ? `· Section ${student.section}` : ''} · Roll No. {student?.rollNo}
        </div>
      </div>

      {/* Fee Status Banner */}
      {feeStatus && (
        <div className={`fee-banner ${feeType}`} onClick={() => navigate('/student/fees')}>
          <div className="fb-left">
            <div className={`fb-icon ${feeType}`}>
              {feeType==='clear' ? <CheckCircleIcon/> : <ExclamationTriangleIcon/>}
            </div>
            <div>
              <div className="fb-title" style={{color:feeColor}}>{feeTitle}</div>
              <div className="fb-sub" style={{color:feeColor+'99'}}>{feeSubtitle}</div>
            </div>
          </div>
          {feeAmt > 0 && (
            <div className="fb-amount" style={{color:feeColor}}>{fmtMoney(feeAmt)}</div>
          )}
        </div>
      )}

      {/* Stat Cards */}
      <div className="stat-grid">
        <div className="stat-card" onClick={()=>navigate('/student/attendance')}>
          <div className="s-icon green"><CalendarDaysIcon/></div>
          <div className="s-label">Attendance</div>
          <div className="s-value" style={{color:attPct>=80?'#10c98e':attPct>=60?'#f59e0b':'#ef4444'}}>{attPct}%</div>
          <div className="progress-bar" style={{marginTop:6}}>
            <div className="progress-fill" style={{width:`${attPct}%`,background:attPct>=80?'#10c98e':attPct>=60?'#f59e0b':'#ef4444'}}/>
          </div>
          <div className="s-sub">{attendance?.present||0} / {attendance?.totalDays||0} days</div>
        </div>
        <div className="stat-card" onClick={()=>navigate('/student/results')}>
          <div className="s-icon blue"><AcademicCapIcon/></div>
          <div className="s-label">Recent Results</div>
          <div className="s-value">{recentResults?.length ?? 0}</div>
          <div className="s-sub">assessments marked</div>
        </div>
        <div className="stat-card" onClick={()=>navigate('/student/quizzes')}>
          <div className="s-icon purple"><BookOpenIcon/></div>
          <div className="s-label">Quizzes</div>
          <div className="s-value">
            {quizSummary?.attempted ?? 0}
            <span style={{fontSize:'.9rem',color:'#9094a8'}}>/{quizSummary?.total??0}</span>
          </div>
          <div className="s-sub">attempted</div>
        </div>
        <div className="stat-card" onClick={()=>navigate('/student/datesheets')}>
          <div className="s-icon amber"><ClipboardDocumentListIcon/></div>
          <div className="s-label">Upcoming Exams</div>
          <div className="s-value">{upcomingExams?.length ?? 0}</div>
          <div className="s-sub">scheduled</div>
        </div>
      </div>

      {/* Two panels */}
      <div className="two-col">
        {/* Recent results */}
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Recent Results</div>
            <span className="panel-link" onClick={()=>navigate('/student/results')}>View all</span>
          </div>
          {!recentResults?.length ? (
            <div style={{padding:'2rem',textAlign:'center',color:'#9094a8',fontSize:'.78rem'}}>No results yet.</div>
          ) : (
            <div className="results-list">
              {recentResults.slice(0,5).map((r,i)=>{
                const pct=r.maxMarks?Math.round((r.marksObtained/r.maxMarks)*100):0;
                const color=pct>=80?'#10c98e':pct>=50?'#f59e0b':'#ef4444';
                return(
                  <div key={i} className="result-row">
                    <div className="result-subject">{r.subject}</div>
                    <span className="result-type">{r.assessmentType}</span>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div className="result-bar-wrap">
                        <div className="result-bar-fill" style={{width:`${pct}%`,background:color}}/>
                      </div>
                      <div className="result-score" style={{color}}>{r.marksObtained}/{r.maxMarks}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming exams with countdown */}
        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Upcoming Exams</div>
            <span className="panel-link" onClick={()=>navigate('/student/datesheets')}>View all</span>
          </div>
          {!upcomingExams?.length ? (
            <div style={{padding:'2rem',textAlign:'center',color:'#9094a8',fontSize:'.78rem'}}>No upcoming exams.</div>
          ) : (
            <div className="exam-list">
              {upcomingExams.slice(0,4).map((e,i)=>{
                const daysLeft=Math.ceil((new Date(e.date)-new Date())/86400000);
                const color=daysLeft<=3?'#ef4444':daysLeft<=7?'#f59e0b':'#3b6ef8';
                const bg   =daysLeft<=3?'#fef2f2':daysLeft<=7?'#fffbeb':'#eef2ff';
                return(
                  <div key={i} className="exam-card">
                    <div className="exam-info">
                      <div className="exam-name">{e.title||e.subject}</div>
                      <div className="exam-meta">{e.class?.name||student?.class} · {fmtDate(e.date)}</div>
                    </div>
                    <div className="exam-countdown" style={{background:bg,borderRadius:10,padding:'6px 10px',minWidth:52}}>
                      <div className="countdown-val" style={{color}}>{daysLeft===0?'📅':daysLeft===1?'1':daysLeft}</div>
                      <div className="countdown-label" style={{color}}>{daysLeft===0?'Today':daysLeft===1?'day left':'days'}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Notices */}
          {recentNotices?.length > 0 && (
            <>
              <div style={{borderTop:'1px solid #f0f2f8'}}>
                <div className="panel-head" style={{borderBottom:'none',paddingBottom:6}}>
                  <div className="panel-title">Notices</div>
                  <span className="panel-link" onClick={()=>navigate('/student/notices')}>View all</span>
                </div>
              </div>
              {recentNotices.slice(0,2).map(n=>(
                <div key={n._id} className="list-item">
                  <div className="list-dot amber"/>
                  <div><div className="list-title">{n.title}</div><div className="list-sub">{fmtDate(n.createdAt)}</div></div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;